import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, combineLatest, map, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { emitOnce, filterNil } from '@ngneat/elf';
import { SessionRepository } from '@app-core/state/session.repository';
import { CountryCode } from '@app-shared/enums/country-codes.enum';

import { ICountry } from './country.model';
import { CountryRepository } from './country.repository';

@Injectable({ providedIn: 'root' })
export class CountryService {
  private readonly http = inject(HttpClient);
  private readonly countryRepo = inject(CountryRepository);
  private readonly sessionRepo = inject(SessionRepository);

  /**
   * Check to see if active country is Switzerland or if region includes Switzerland as country.
   */
  readonly isSwitzerland$ = combineLatest([
    this.countryRepo.activeCountry$,
    this.sessionRepo.authUser$,
  ]).pipe(
    filterNil(),
    map(([activeCountry, authUser]) => {
      const isSwitzerland =
        activeCountry?.country_code === CountryCode.SWITZERLAND ||
        authUser?.region?.countries.some(
          country => country.country_code === CountryCode.SWITZERLAND,
        );

      return isSwitzerland;
    }),
  );

  /**
   * Check to see if active country is Germany or if region includes Germany as country.
   */
  readonly isGermany$ = combineLatest([
    this.countryRepo.activeCountry$,
    this.sessionRepo.authUser$,
  ]).pipe(
    filterNil(),
    map(([activeCountry, authUser]) => {
      const isGermany =
        activeCountry?.country_code === CountryCode.GERMANY ||
        authUser?.region?.countries.some(
          country => country.country_code === CountryCode.GERMANY,
        );

      return isGermany;
    }),
  );
  /**
   * Gets a list of all countries and saves them to the store
   *
   * GET /product/country
   *
   * @returns a list of countries
   */
  getCountries(): Observable<ICountry[]> {
    return this.http.get<ICountry[]>(environment.apiUrl + `/country`).pipe(
      tap(data => {
        emitOnce(() => {
          this.countryRepo.setCountries(data);
          this.countryRepo.setCountryCodes(data);
        });
      }),
    );
  }
}
